import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import mapValues from 'lodash/mapValues';

import { DEFAULT_MIN_OPEN_PRICE } from 'common/entities/ticketType/defaultValues';
import { getTicketTypeById } from 'common/entities/ticketType/selectors';
import transformValueToPixels from 'common/compounds/FormRenderer/utils/transformValueToPixels';

import {
    DEFAULT_INPUT_BORDER_COLOR,
    DEFAULT_INPUT_BORDER_COLOR_HOVER,
    DEFAULT_INPUT_ANSWER_COLOR,
    DEFAULT_INPUT_PLACEHOLDER_COLOR,
    DEFAULT_FONT,
    DEFAULT_SMALL_TICKET_FONT_SIZE,
    DEFAULT_INPUT_BORDER_WIDTH,
    DEFAULT_INPUT_BORDER_RADIUS,
    DEFAULT_INPUT_BACKGROUND_COLOR,
} from 'common/entities/form/styleConfiguration/defaultValues';
import { formatCurrency, getCurrencySymbol } from 'utils/currency';
import { getInstance as getBootstrap } from 'utils/bootstrap';
import { SuiTextInput, SuiThemeProvider } from '@splashthat/sui';
import { createSuiTheme } from '@splashthat/sui/lib/theme';
import { TicketTypePriceContainer } from './TicketTypePrice.styled';
import { getTicketTypeQuantityValue, getTicketTypeOpenPriceValue } from '../../../../../selectors/form';
import { updateFormGroupValue } from '../../../../../actions/form';
import { TICKET_VALUE_OPEN_PRICE } from '../../../../../constants/ticketTypes';
import parseFontSize from '../../../../../utils/parseFontSize';

export const SOLD_OUT_LABEL = 'SOLD OUT';

class TicketTypePriceQuantity extends React.Component {
    static propTypes = {
        priceStyles: PropTypes.object,
        id: PropTypes.node,
        disabled: PropTypes.bool,
        price: PropTypes.string,
        openPrice: PropTypes.string,
        priceHidden: PropTypes.string,
        isOpenPrice: PropTypes.bool.isRequired,
        minOpenPrice: PropTypes.number.isRequired,
        maxOpenPrice: PropTypes.number.isRequired,
        updateFormGroupValue: PropTypes.func.isRequired,
        qaId: PropTypes.string.isRequired,
        quantityStyles: PropTypes.object,
    };

    static defaultProps = {
        id: null,
        price: null,
        quantity: null,
        openPrice: null,
        priceHidden: null,
        disabled: false,
        priceStyles: null,
        quantityStyles: null,
    };

    onHandleOpenPriceChange = (event, value) => {
        this.props.updateFormGroupValue(this.props.id, TICKET_VALUE_OPEN_PRICE, value);
    };

    getPriceValue = () => {
        const { priceHidden, qaId, price } = this.props;

        const bootstrap = getBootstrap();
        const locale = bootstrap.get('locale') || navigator.language;
        const currencyCode = bootstrap.get('currency.code') || 'USD';

        const shouldRenderPrice = parseInt(priceHidden, 10);

        if (shouldRenderPrice) return null;

        return <p data-qa={`${qaId}__price__value`}>{formatCurrency(currencyCode, locale)(price)}</p>;
    };

    getTicketCurrencySymbol = () => {
        const bootstrap = getBootstrap();
        return getCurrencySymbol(bootstrap.get('currency.code') || 'USD');
    };

    render() {
        const { priceStyles, isOpenPrice, minOpenPrice, maxOpenPrice, disabled, openPrice, qaId, quantityStyles } =
            this.props;

        const borderColor = get(quantityStyles, 'colors.default.value', DEFAULT_INPUT_BORDER_COLOR);
        const borderColorHover = get(quantityStyles, 'colors.hover.value', DEFAULT_INPUT_BORDER_COLOR_HOVER);
        const answerColor = get(quantityStyles, 'fontColors.answer.value', DEFAULT_INPUT_ANSWER_COLOR);
        const placeholderColor = get(quantityStyles, 'fontColors.placeholder.value', DEFAULT_INPUT_PLACEHOLDER_COLOR);
        const fontFamily = get(quantityStyles, 'font.value', DEFAULT_FONT);
        const fontSize = get(quantityStyles, 'fontSize', DEFAULT_SMALL_TICKET_FONT_SIZE);
        const inputBackgroundColorDefault = get(
            quantityStyles,
            'inputBackgroundColors.default.value',
            DEFAULT_INPUT_BACKGROUND_COLOR,
        );
        const inputBackgroundColorHover = get(
            quantityStyles,
            'inputBackgroundColors.hover.value',
            DEFAULT_INPUT_BACKGROUND_COLOR,
        );
        const borderWidths = get(quantityStyles, 'sizes', {
            top: DEFAULT_INPUT_BORDER_WIDTH,
            bottom: DEFAULT_INPUT_BORDER_WIDTH,
            right: DEFAULT_INPUT_BORDER_WIDTH,
            left: DEFAULT_INPUT_BORDER_WIDTH,
        });
        const borderRadius = get(quantityStyles, 'radius', DEFAULT_INPUT_BORDER_RADIUS);

        return (
            <TicketTypePriceContainer
                data-qa={`${qaId}__price`}
                fontFamily={get(priceStyles, 'font.value')}
                fontSize={get(priceStyles, 'fontSize')}
                color={get(priceStyles, 'color.value')}
            >
                {isOpenPrice ? (
                    <SuiThemeProvider
                        theme={createSuiTheme({
                            components: {
                                // FIXME having to set the theme on suiField to edit
                                //  SuiTextInput doesn't look very intuitive
                                suiField: {
                                    variants: {
                                        base: {
                                            border: {
                                                radius: transformValueToPixels(borderRadius),
                                                color: borderColor,
                                                hover: {
                                                    color: borderColorHover,
                                                },
                                                focus: {
                                                    color: borderColorHover,
                                                },
                                                ...mapValues(borderWidths, (size) => transformValueToPixels(size)),
                                            },
                                            font: {
                                                family: fontFamily,
                                                size: parseFontSize(fontSize),
                                                color: answerColor,
                                                placeholderColor,
                                            },
                                            background: {
                                                color: inputBackgroundColorDefault,
                                                hover: {
                                                    color: inputBackgroundColorHover,
                                                },
                                            },
                                        },
                                    },
                                },
                            },
                        })}
                    >
                        <SuiTextInput
                            data-qa={`${qaId}__price__input`}
                            fullWidth
                            type="number"
                            placeholder={`${this.getTicketCurrencySymbol()} --`}
                            min={minOpenPrice || DEFAULT_MIN_OPEN_PRICE}
                            max={maxOpenPrice || ''}
                            disabled={disabled}
                            value={openPrice}
                            onChange={this.onHandleOpenPriceChange}
                        />
                    </SuiThemeProvider>
                ) : (
                    this.getPriceValue()
                )}
            </TicketTypePriceContainer>
        );
    }
}

const mapStateToProps = (state, props) => {
    const quantity = getTicketTypeQuantityValue(state, { id: props.id });
    const openPrice = getTicketTypeOpenPriceValue(state, { id: props.id });
    const ticketType = getTicketTypeById(state, props.id);
    const priceHidden = get(ticketType, 'priceHidden');

    return {
        quantity,
        openPrice,
        priceHidden,
    };
};

const mapDispatchToProps = { updateFormGroupValue };

export default connect(mapStateToProps, mapDispatchToProps)(TicketTypePriceQuantity);
